<script setup>
import { computed, onMounted, ref } from 'vue';
import RichTextEditor from '@/js/core/components/parts/editor/RichTextEditor.vue';
import FocalPointPicker from '@/js/core/components/parts/trays/FocalPointPicker.vue';
import TrayAccessFields from '@/js/core/components/parts/trays/TrayAccessFields.vue';
import UserSearchInput from '@/js/core/components/parts/trays/UserSearchInput.vue';
import { useTrayManagerEdit } from '@/js/core/composables/useTrayManagerEdit';

const props = defineProps({
  tray: { type: Object, required: true },
  settingsInitialised: { type: Boolean, default: false },
  editTitle: { type: String, required: true },
  editDescription: { type: String, required: true },
  editVisibility: { type: String, required: true },
  editCommentAccess: { type: String, required: true },
  editVisibleUsers: { type: Array, required: true },
  editCommentableUsers: { type: Array, required: true },
  editImageUrl: { type: String, default: null },
  editFocalPoint: { type: Object, default: null },
  editFileName: { type: String, default: '' },
  editAlt: { type: String, default: '' },
  editCaption: { type: String, default: '' },
  editFeaturedMediaAltError: { type: String, default: '' },
  editError: { type: String, default: '' },
  errorField: { type: String, default: '' },
  fieldErrors: { type: Object, default: () => ({}) },
  uploadError: { type: String, default: '' },
  isUploading: { type: Boolean, default: false },
  isSaving: { type: Boolean, default: false },
});

defineEmits([
  'update:editTitle',
  'update:editDescription',
  'update:editVisibility',
  'update:editCommentAccess',
  'update:editVisibleUsers',
  'update:editCommentableUsers',
  'update:editFocalPoint',
  'update:editAlt',
  'update:editCaption',
  'save',
  'cancel',
  'file-change',
  'file-drop',
  'drag-over',
  'remove-image',
]);

const {
  managers,
  pendingAdds,
  pendingRemoves,
  saveErrors,
  loading,
  error,
  isInitialised,
  hasPendingChanges,
  excludedManagerUuids,
  isOwner,
  isSelf,
  handleAddManager,
  handleRemoveManager,
  fetchManagers,
  runManagerSaves,
} = useTrayManagerEdit(() => props.tray);

const isReady = computed(() => props.settingsInitialised && isInitialised.value);

// The access fields list managers alongside the selected users, so they reflect
// the queued manager changes rather than what the server last returned.
const accessRoleUsers = computed(() => {
  const seen = new Set();
  const users = [];

  for (const manager of managers.value) {
    if (pendingRemoves.value.includes(manager.uuid) || seen.has(manager.user_uuid)) {
      continue;
    }

    seen.add(manager.user_uuid);
    users.push({ uuid: manager.user_uuid, name: manager.user_name });
  }

  for (const user of pendingAdds.value) {
    if (seen.has(user.uuid)) {
      continue;
    }

    seen.add(user.uuid);
    users.push({ uuid: user.uuid, name: user.name });
  }

  return users;
});

const headingRef = ref(null);
const errorId = 'tray-edit-form-error';

const describedBy = field => (props.errorField === field ? errorId : undefined);
const invalidFor = field => (props.errorField === field ? 'true' : undefined);

const focusFirst = () => headingRef.value?.focus();

const isPendingRemove = manager => pendingRemoves.value.includes(manager.uuid);

defineExpose({ hasPendingChanges, runManagerSaves, focusFirst });

onMounted(fetchManagers);
</script>

<template>
  <div class="tray-edit-form__section">
    <div v-if="editError" :id="errorId" class="tray-edit-form__error" role="alert">
      {{ editError }}
    </div>

    <div class="tray-edit-form__form">
      <h3 ref="headingRef" class="tray-edit-form__section-heading" tabindex="-1">Tray Settings</h3>

      <div class="tray-form__group">
        <label for="edit-tray-title" class="tray-form__label">Title</label>
        <input
          id="edit-tray-title"
          :value="editTitle"
          type="text"
          class="tray-form__input"
          :aria-invalid="invalidFor('title')"
          :aria-describedby="describedBy('title')"
          @input="$emit('update:editTitle', $event.target.value)"
        />
      </div>

      <div class="tray-form__group">
        <label class="tray-form__label">Featured media</label>
        <template v-if="editImageUrl">
          <FocalPointPicker
            :model-value="editFocalPoint"
            :image-url="editImageUrl"
            :file-name="editFileName"
            :alt="editAlt"
            :caption="editCaption"
            alt-required
            :alt-error="editFeaturedMediaAltError"
            id-prefix="edit-featured"
            @update:model-value="$emit('update:editFocalPoint', $event)"
            @update:alt="$emit('update:editAlt', $event)"
            @update:caption="$emit('update:editCaption', $event)"
            @remove="$emit('remove-image')"
          />
          <p v-if="isUploading" class="tray-form__hint">Uploading...</p>
        </template>
        <template v-else>
          <div
            class="tray-form__dropzone"
            @drop="$emit('file-drop', $event)"
            @dragover="$emit('drag-over', $event)"
          >
            <input
              id="edit-featured-media"
              type="file"
              class="tray-form__file-input"
              accept="image/jpeg,image/png,image/gif,image/webp"
              aria-describedby="edit-featured-media-size-note"
              @change="$emit('file-change', $event)"
            />
            <label for="edit-featured-media" class="tray-form__dropzone-label">
              Drop files here or click to browse
            </label>
          </div>
        </template>
        <p v-if="uploadError" class="tray-form__field-error">{{ uploadError }}</p>
        <p id="edit-featured-media-size-note" class="tray-form__hint">
          The maximum allowed file size is 2MB.
        </p>
      </div>

      <div class="tray-form__group">
        <label id="tray-edit-description-label" class="tray-form__label">Description</label>
        <div class="tray-edit-form__editor-wrapper tray-form__editor">
          <RichTextEditor
            :model-value="editDescription"
            :placeholder="''"
            editor-class="tray-form__textarea"
            toolbar-class="tray-form__toolbar"
            button-class="tray-form__toolbar-btn"
            aria-labelledby="tray-edit-description-label"
            :aria-invalid="errorField === 'description'"
            :aria-describedby="describedBy('description')"
            @update:model-value="$emit('update:editDescription', $event)"
          />
        </div>
      </div>

      <TrayAccessFields
        :visible-to="editVisibility"
        :comment-access="editCommentAccess"
        :visible-users="editVisibleUsers"
        :commentable-users="editCommentableUsers"
        :role-users="accessRoleUsers"
        :errors="fieldErrors"
        id-prefix="edit-tray"
        @update:visible-to="$emit('update:editVisibility', $event)"
        @update:comment-access="$emit('update:editCommentAccess', $event)"
        @update:visible-users="$emit('update:editVisibleUsers', $event)"
        @update:commentable-users="$emit('update:editCommentableUsers', $event)"
      />

      <div class="tray-edit-form__managers">
        <h3 class="tray-edit-form__section-heading">Managers</h3>

        <div v-if="error" class="tray-edit-form__error">{{ error }}</div>

        <p v-if="loading" class="tray-edit-form__loading">Loading managers...</p>

        <template v-else>
          <ul v-if="managers.length || pendingAdds.length" class="tray-edit-form__manager-list">
            <li
              v-for="manager in managers"
              :key="manager.uuid"
              class="tray-edit-form__manager-item"
              :class="{
                'tray-edit-form__manager-item--pending-remove': isPendingRemove(manager),
              }"
            >
              <div class="tray-edit-form__manager-info">
                <span class="tray-edit-form__manager-name">{{ manager.user_name }}</span>
                <span class="tray-edit-form__manager-email">{{ manager.user_email }}</span>
              </div>
              <button
                v-if="(isOwner() || isSelf(manager)) && !isPendingRemove(manager)"
                type="button"
                class="tray-edit-form__manager-action"
                @click="handleRemoveManager(manager)"
              >
                {{ isOwner() ? 'Remove' : 'Leave' }}
              </button>
            </li>
            <li
              v-for="user in pendingAdds"
              :key="'add-' + user.uuid"
              class="tray-edit-form__manager-item tray-edit-form__manager-item--pending-add"
            >
              <div class="tray-edit-form__manager-info">
                <span class="tray-edit-form__manager-name">{{ user.name }}</span>
                <span class="tray-edit-form__manager-email">{{ user.email }}</span>
              </div>
              <span class="tray-edit-form__pending-label">Pending</span>
            </li>
          </ul>

          <p v-else class="tray-edit-form__empty">No managers assigned yet.</p>
        </template>

        <div class="tray-edit-form__add-section">
          <UserSearchInput
            input-id="tray-manager-search"
            label="Add a manager"
            :excluded-uuids="excludedManagerUuids"
            @select="handleAddManager"
          />
        </div>
      </div>

      <div v-if="saveErrors.length" class="tray-edit-form__save-errors" aria-live="polite">
        <p v-for="(err, i) in saveErrors" :key="i">{{ err }}</p>
      </div>

      <div class="tray-edit-form__actions">
        <button
          class="tray-edit-form__save"
          type="button"
          :disabled="!isReady || isSaving || isUploading"
          @click="$emit('save')"
        >
          {{ isSaving ? 'Updating...' : 'Update' }}
        </button>
        <button
          class="tray-edit-form__cancel"
          type="button"
          :disabled="isSaving"
          @click="$emit('cancel')"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>
